import { Check } from "lucide-react";

import { ClientPlanScreen, TodayScreen } from "@/features/platform/components/app-screens";
import { ScheduleDesk } from "@/features/platform/components/desks";
import { Laptop, Phone } from "@/features/platform/components/devices";
import { Accent, displayTitle, Eyebrow, Section } from "@/features/platform/components/landing-ui";
import { DirectoryMap, PassesArt } from "@/features/platform/components/pictures";
import { Stage } from "@/features/platform/components/stage";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const PROMISES = [() => m.platform_step_account_body(), () => m.pricing_page_promise_change()];

/** The words that open `/pricing`, inside the head card; the plans follow right under it. */
export const PricingHero = () => (
  <div className="relative mx-auto max-w-[880px] px-1 pt-14 pb-2 text-center md:pt-20">
    <Eyebrow>{m.platform_pricing_title()}</Eyebrow>
    <h1 className={cn(displayTitle, "mt-6")}>
      {m.pricing_page_title_lead()} <Accent>{m.pricing_page_title_tail()}</Accent>
    </h1>
    <p className="mx-auto mt-6 max-w-[52ch] text-lg leading-[1.55] text-pretty text-muted-foreground">
      {m.platform_pricing_aside()}
    </p>
    <ul className="mt-8 flex flex-wrap justify-center gap-2">
      {PROMISES.map((promise) => (
        <li
          key={promise()}
          className="inline-flex h-10 items-center gap-2.5 rounded-full border border-border bg-background/70 pr-4 pl-1.5 text-sm font-medium backdrop-blur-xl"
        >
          <span className="grid size-7 place-items-center rounded-full bg-primary text-primary-foreground">
            <Check aria-hidden className="size-3.5" strokeWidth={3} />
          </span>
          {promise()}
        </li>
      ))}
    </ul>
  </div>
);

/** What the money buys, after the plans: the tools a coach would otherwise pay for one by one. */
export const PricingValue = () => (
  <Section>
    <div className="mb-10 max-w-[780px] px-1">
      <Eyebrow>{m.pricing_page_value_eyebrow()}</Eyebrow>
      <h2 className={cn(displayTitle, "mt-5")}>
        {m.pricing_page_value_lead()} <Accent>{m.pricing_page_value_tail()}</Accent>
      </h2>
      <p className="mt-5 max-w-[56ch] text-lg leading-[1.55] text-pretty text-muted-foreground">
        {m.pricing_page_value_lede()}
      </p>
    </div>
    <div className="grid gap-3 md:grid-cols-2">
      <Stage
        side
        title={m.pricing_page_schedule_title()}
        lead={m.pricing_page_schedule_lead()}
        body={m.pricing_page_schedule_body()}
        className="md:col-span-2"
        artClassName="flex items-center justify-center px-5 pt-2 pb-12 lg:py-16 lg:pr-12 lg:pl-0"
      >
        <div className="relative w-full max-w-[800px] pr-[10%]">
          <Laptop>
            <ScheduleDesk />
          </Laptop>
          <Phone className="absolute right-0 -bottom-[7%] w-[20%]">
            <TodayScreen />
          </Phone>
        </div>
      </Stage>
      <Stage lead={m.features_pay_lead()} body={m.features_pay_body()} className="h-[680px] md:h-[620px]">
        <PassesArt />
      </Stage>
      <Stage lead={m.features_plan_lead()} body={m.features_plan_body()} className="h-[680px] md:h-[620px]" fade>
        <Phone className="absolute top-0 left-1/2 w-[min(300px,82%)] -translate-x-1/2">
          <ClientPlanScreen />
        </Phone>
      </Stage>
      <Stage
        side
        flip
        title={m.pricing_page_directory_title()}
        lead={m.features_directory_lead()}
        body={m.features_directory_body()}
        className="md:col-span-2 lg:min-h-[560px]"
        artClassName="relative min-h-[460px] lg:min-h-0"
      >
        <DirectoryMap />
      </Stage>
    </div>
  </Section>
);
